import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { yieldCallback } from '@zillow/yield-callback';

import AppActions from 'app/shared/flux/actions/AppActions';

import ConversationActions from 'app/shared/flux/actions/ConversationActions';
import HeaderNavMdAndUp from 'app/shared/modules/navigation/header/HeaderNavMdAndUp';
import HeaderNavMobile from 'app/shared/modules/navigation/header/HeaderNavMobile';
import SkipToMain from 'app/shared/modules/SkipToMain';
import routeUtils from 'app/shared/utils/routeUtils';
import { zindex } from 'app/shared/styles/_zindex';
import { colors } from 'app/shared/styles/_colors';
import { headerHeights } from 'app/shared/styles/_variables';
import { viewports } from 'app/shared/styles/_breakpoints';
import { isFeatureFlagEnabled } from 'app/shared/utils/featureFlagUtils';
import HeaderMockInquiriesWarning from './HeaderMockInquiryWarning';

const StyledHeaderNav = styled.div`
  height: ${headerHeights['mobile-header-height']};
  @media ${viewports['md-and-up']} {
    height: ${headerHeights['desktop-header-height']};
  }
`;

const StyledHeaderNavWrapper = styled.div`
  left: 0;
  background: ${colors['$hpx-white']};
  z-index: ${zindex['$z-index-loader']};
  right: 0;
  top: 0;
  position: fixed;
`;

interface RootState {
  location: { current: any };
  app: { device: { screenWidth: string }; hamburger: boolean };
  user: { loggedIn: boolean; unreadMsgCount: number; info: any };
}

const HeaderNav: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [mockInquiriesWarning, setMockInquiriesWarning] = useState(false);

  const savedActiveElement = React.useRef<HTMLElement | null>(null);

  const { loggedIn, userInfo, isMobile, unreadMsgCount } = useSelector((state: RootState) => ({
    loggedIn: state.user.loggedIn,
    userInfo: state.user.info,
    isMobile: state.app.device.screenWidth === 'sm',
    unreadMsgCount: state.user.unreadMsgCount,
  }));

  const pathname = location.pathname;

  const fetchMessageCount = useCallback(() => {
    dispatch(ConversationActions.fetchMessageCount());
  }, [dispatch]);

  useEffect(() => {
    fetchMessageCount();
  }, [fetchMessageCount, pathname]);

  useEffect(() => {
    if (isFeatureFlagEnabled('mockInquiries')) {
      setMockInquiriesWarning(true);
    }
  }, []);

  const handleOpenHamburgerNav = yieldCallback(() => {
    savedActiveElement.current = document.activeElement as HTMLElement;
    dispatch(AppActions.activateHamburger());
  });

  const handleCloseHamburgerNav = () => {
    if (savedActiveElement.current) {
      savedActiveElement.current.focus();
    }
    dispatch(AppActions.deactivateHamburger());
    savedActiveElement.current = null;
  };

  const isPadOrBuildingUrl = routeUtils.isPadOrBuildingUrl(pathname);
  const isHaloUrl = pathname.startsWith('/halo');

  if (isHaloUrl) {
    return null;
  }

  if (isPadOrBuildingUrl && isMobile) {
    return null;
  }

  return (
    <>
      <StyledHeaderNav>
        <SkipToMain />
        <StyledHeaderNavWrapper>
          {mockInquiriesWarning && <HeaderMockInquiriesWarning />}
          {isMobile ? (
            <HeaderNavMobile
              onCloseHamburgerNav={handleCloseHamburgerNav}
              onOpenHamburgerNav={handleOpenHamburgerNav}
              unreadMsgCount={unreadMsgCount}
            />
          ) : (
            <HeaderNavMdAndUp
              loggedIn={loggedIn}
              userInfo={userInfo}
              onOpenHamburgerNav={handleOpenHamburgerNav}
              onCloseHamburgerNav={handleCloseHamburgerNav}
              unreadMsgCount={unreadMsgCount}
            />
          )}
        </StyledHeaderNavWrapper>
      </StyledHeaderNav>
      <div id="skip-topnav-target" tabIndex={-1} />
    </>
  );
};

export default HeaderNav;
